/**
 * Best-effort device / GPU fingerprint for crash triage.
 *
 * The Pixel-9 "Starting AR…" hang produces no useful telemetry partly because
 * a captured event can't tell a Pixel 9 from a Pixel 7 — same Chrome major,
 * same config. This collects the fields that actually discriminate hardware:
 * the unmasked GPU renderer string, device memory, DPR, and the parsed
 * Chrome / Android versions. Attached as Sentry tags + context in main.tsx so
 * EVERY captured event (including the launch-marker and watchdog reports) is
 * comparable across devices.
 *
 * Every read is wrapped — this must never throw and never block boot.
 */

export interface DeviceContext {
  gpuRenderer?: string;
  gpuVendor?: string;
  deviceMemory?: number;
  hardwareConcurrency?: number;
  dpr?: number;
  chromeMajor?: number;
  androidVersion?: string;
  isAndroid?: boolean;
  ua?: string;
}

/** Read the unmasked WebGL renderer/vendor via WEBGL_debug_renderer_info. */
function readGpu(): { renderer?: string; vendor?: string } {
  try {
    const canvas = document.createElement("canvas");
    const gl = (canvas.getContext("webgl") || canvas.getContext("experimental-webgl")) as WebGLRenderingContext | null;
    if (!gl) return {};
    const dbg = gl.getExtension("WEBGL_debug_renderer_info");
    const renderer = dbg ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) : undefined;
    const vendor = dbg ? String(gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL)) : undefined;
    // Release the throwaway context promptly rather than waiting for GC.
    gl.getExtension("WEBGL_lose_context")?.loseContext();
    return { renderer, vendor };
  } catch {
    return {};
  }
}

export function collectDeviceContext(): DeviceContext {
  const ctx: DeviceContext = {};
  try {
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
    ctx.ua = ua;
    ctx.isAndroid = /Android/.test(ua);

    const chrome = ua.match(/Chrome\/(\d+)/);
    if (chrome) ctx.chromeMajor = parseInt(chrome[1], 10);

    const android = ua.match(/Android (\d+(?:\.\d+)?)/);
    if (android) ctx.androidVersion = android[1];

    ctx.deviceMemory = (navigator as unknown as { deviceMemory?: number }).deviceMemory;
    ctx.hardwareConcurrency = navigator.hardwareConcurrency;
    ctx.dpr = typeof window !== "undefined" ? window.devicePixelRatio : undefined;

    const gpu = readGpu();
    ctx.gpuRenderer = gpu.renderer;
    ctx.gpuVendor = gpu.vendor;
  } catch {
    /* never throw */
  }
  return ctx;
}
